import React, { useState, useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { useDetailedSatisfaction, useSatisfactionCategories } from '../../hooks/useSatisfactionQueries';
import { useTheme } from '../../contexts/ThemeContext';
import { ChartControls } from '../ui';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { getChartColors, getChartBackgroundColor, getChartJsOptions, formatTooltipLabel, mergeChartOptions } from '../../utils/themeHelpers';
import { TYPOGRAPHY_CONFIG } from '../../config/typography.config';
import { TimePeriod } from '../../types/api';
import { logger } from '../../utils/logger';

interface DetailedSatisfactionProps {
  branchId?: number;
  timeRange?: TimePeriod;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
}

const DetailedSatisfaction: React.FC<DetailedSatisfactionProps> = ({
  branchId = 0,
  timeRange,
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data: detailedData, isLoading: detailedLoading, error: detailedError } = useDetailedSatisfaction(branchId, timeRange);
  const { data: categoriesData, isLoading: categoriesLoading, error: categoriesError } = useSatisfactionCategories(branchId);
  
  // Component metadata
  const componentId = 'detailed-satisfaction';
  const componentTitle = 'شاخص‌های رضایت مشتریان به تفکیک دسته‌بندی';
  
  const [selectedMetricCategories, setSelectedMetricCategories] = useState<string[]>([]);
  const [metricCategoryGroups, setMetricCategoryGroups] = useState<{[key: string]: string[]}>({});
  const [activeMetricGroup, setActiveMetricGroup] = useState<string>('');
  const [isAllQuestionsCollapsed, setIsAllQuestionsCollapsed] = useState<boolean>(true);

  const isLoading = detailedLoading || categoriesLoading;
  const error = detailedError || categoriesError;

  // Handle error
  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  // Process categories data
  useEffect(() => {
    logger.debug('Categories Data', 'DetailedSatisfaction', categoriesData);
    if (categoriesData && categoriesData.groups) {
      const metricGroups = categoriesData.groups || {};
      logger.debug('Metric Groups', 'DetailedSatisfaction', metricGroups);
      
      // Create "همه سوالات" group with all categories
      const allCategories = Object.values(metricGroups).flat();
      const uniqueCategories = Array.from(new Set(allCategories));
      
      const metricGroupsWithAll = {
        ...metricGroups,
        'همه سوالات': uniqueCategories
      };
      
      setMetricCategoryGroups(metricGroupsWithAll);
      
      // Set first group as active by default (not "همه سوالات")
      const groupNames = Object.keys(metricGroups);
      if (groupNames.length > 0) {
        setActiveMetricGroup(groupNames[0]);
        setSelectedMetricCategories(metricGroups[groupNames[0]]);
      }
    } else {
      // Fallback to basic grouping
      logger.debug('No categories data, using fallback', 'DetailedSatisfaction');
      const metricGroups = { 'همه دسته‌ها': [] };
      setMetricCategoryGroups(metricGroups);
      setActiveMetricGroup('همه دسته‌ها');
      setSelectedMetricCategories([]);
    }
  }, [categoriesData]);

  // Use theme-aware colors
  const chartColors = getChartColors(currentTheme);

  // Helper function to get emoji based on satisfaction percentage
  const getSatisfactionEmoji = (percentage: number): string => {
    if (percentage >= 90) return '😍';
    if (percentage >= 80) return '😊';
    if (percentage >= 70) return '🙂';
    if (percentage >= 60) return '😐';
    if (percentage >= 50) return '😕';
    if (percentage >= 40) return '☹️';
    return '😢';
  };

  // Calculate average for a metric in the current data
  const calculateMetricAverage = (metricName: string) => {
    if (!detailedData || !detailedData.length) return 0;
    const satisfiedCount = detailedData.reduce((acc: number, item: any) => acc + Number(item[metricName]["satisfiedCount"] || 0), 0);
    const survaycount = detailedData.reduce((acc: number, item: any) => acc + Number(item[metricName]["survaycount"] || 0), 0);
    return (satisfiedCount / survaycount * 100).toFixed(2);
  };

  // Handle metric group selection
  const handleMetricGroupChange = (groupName: string) => {
    setActiveMetricGroup(groupName);
    setSelectedMetricCategories(metricCategoryGroups[groupName] || []);
    // Reset collapsed state when switching groups
    if (groupName === 'همه سوالات') {
      setIsAllQuestionsCollapsed(true);
    }
  };

  // Prepare detailed satisfaction metrics chart data
  const prepareDetailedSatisfactionChart = () => {
    logger.debug('Detailed Data', 'DetailedSatisfaction', detailedData);
    logger.debug('Selected Metric Categories', 'DetailedSatisfaction', selectedMetricCategories);
    if (!detailedData || !detailedData.length || !selectedMetricCategories.length) {
      return { labels: [], datasets: [] };
    }

    // Get months in chronological order
    const months = detailedData
      .map((item: any) => ({ month: item.month, yearMonth: item.yearMonth }))
      .sort((a: any, b: any) => Number(a.yearMonth) - Number(b.yearMonth))
      .map((item: any) => item.month);

    // Create datasets for each satisfaction category
    const datasets = selectedMetricCategories.map((category, index) => {
      const color = chartColors[index % chartColors.length];
      
      return {
        label: category,
        data: months.map(month => {
          const dataPoint = detailedData.find((d: any) => d.month === month);
          return dataPoint ? Number(dataPoint[category]["satisfaction_score"]) : null;
        }),
        borderColor: color,
        backgroundColor: color.startsWith('#') ? 
          `rgba(${parseInt(color.slice(1,3),16)}, ${parseInt(color.slice(3,5),16)}, ${parseInt(color.slice(5,7),16)}, 0.2)` :
          getChartBackgroundColor(index, currentTheme, 0.2),
        borderWidth: 2,
        pointBackgroundColor: color,
        pointBorderColor: currentTheme === 'light' ? '#FFFFFF' : '#27272A',
       pointRadius: 6,
        pointHoverRadius: 10, 
        tension: 0.3,
        fill: false
      };
    });
    
    return { labels: months, datasets };
  };

  // Calculate dynamic Y-axis range
  const calculateDynamicYRange = (data: any[]) => {
    if (!data.length) return { min: 0, max: 105 };
    
    const values = data.flatMap(dataset => 
      dataset.data.filter((value: any) => value !== null && value !== undefined)
    );
    
    if (values.length === 0) return { min: 0, max: 105 };
    
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue;
    
    if (range < 20 && minValue > 20) {
      const padding = Math.max(5, range * 0.2);
      return {
        min: Math.max(0, Math.floor(minValue - padding)),
        max: Math.min(100, Math.ceil(maxValue + padding))
      };
    }
    
    return { min: 0, max: 105 };
  };

  const detailedChartData = prepareDetailedSatisfactionChart();
  const yAxisRange = calculateDynamicYRange(detailedChartData.datasets);

  const chartOptions = mergeChartOptions(getChartJsOptions(currentTheme), {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: !compact
      },
      tooltip: {
        ...getChartJsOptions(currentTheme).plugins.tooltip,
        callbacks: {
          label: (context: any) => {
            if (context.parsed.y === null) return '';
            
            const label = context.dataset.label || '';
            const percentage = context.parsed.y;
            const value = percentage.toFixed(1) + '%';
            const emoji = getSatisfactionEmoji(percentage);
            
            return formatTooltipLabel(label, value);
          }
        }
      }
    },
    scales: {
      ...getChartJsOptions(currentTheme).scales,
      y: {
        ...getChartJsOptions(currentTheme).scales?.y,
        min: yAxisRange.min,
        max: yAxisRange.max,
        title: {
          display: !compact,
          text: 'درصد رضایتمندی',
          color: currentTheme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          font: {
            family: 'var(--font-primary)',
            size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
            weight: TYPOGRAPHY_CONFIG.components.chart.axisLabel.weight,
          }
        },
        ticks: {
          ...getChartJsOptions(currentTheme).scales?.y?.ticks,
          callback: (value: any) => { return value + '%'; },
          stepSize: yAxisRange && (yAxisRange.max - yAxisRange.min) < 30 ? 5 : 10,
        }
      },
      x: {
        ...getChartJsOptions(currentTheme).scales?.x,
        title: {
          display: !compact,
          text: 'ماه',
          color: currentTheme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          font: {
            family: 'var(--font-primary)',
            size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
            weight: TYPOGRAPHY_CONFIG.components.chart.axisLabel.weight,
          }
        },
      }
    },
    elements: (getChartJsOptions(currentTheme) as any).plugins?.elements || {
      point: {
        radius: 0,
        hoverRadius: 8,
        hoverBorderWidth: 3,
        borderWidth: 2,
      },
      line: {
        tension: 0.4,
        borderCapStyle: 'round' as const,
        borderJoinStyle: 'round' as const,
      },
    }
  });

  // Handle loading state
  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="animate-pulse">
          <div className="h-6 bg-bg-secondary rounded w-1/3 mb-4"></div>
          <div className="h-96 bg-bg-secondary rounded"></div>
        </div>
      </div>
    );
  }

  // Handle error or no data state
  if (error || !detailedData || detailedData.length === 0) {
    logger.debug('DetailedSatisfaction not showing because', 'DetailedSatisfaction', {
      error,
      detailedData,
      detailedDataLength: detailedData?.length
    });
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <h3 className="title-card">شاخص‌های رضایت مشتریان به تفکیک دسته‌بندی</h3>
        <div className="text-center py-8 text-text-secondary">
          داده‌ای برای نمایش وجود ندارد
        </div>
      </div>
    );
  }

  return (
    <>
      <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
        {!compact && (
          <ChartControls
            componentId={componentId}
            componentTitle={componentTitle}
            componentType="chart"
            dashboardSource="satisfaction"
            onInfo={() => openGuide(CHART_GUIDES.detailedSatisfaction)}
            onFavorite={() => handleFavoriteToggle({
              componentId,
              componentTitle,
              dashboardSource: 'satisfaction',
              componentType: 'chart',
              description: 'نمودار خطی شاخص‌های رضایت به تفکیک دسته‌بندی',
              props: { branchId, timeRange, onError, showLoading, compact },
              gridLayout: {
                containerClass: 'card-base card-padding'
              }
            })}
            isFavorited={isFavorited(componentId)}
            className="mb-4"
            chartRef={chartRef}
          />
        )}
        <h3 className="title-card">{componentTitle}</h3>
        
        {/* Metric Group Selection Tabs */}
        {!compact && (
          <div className="flex flex-wrap gap-2 mb-6">
            {Object.keys(metricCategoryGroups).map((groupName) => (
              <button 
                key={groupName}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  activeMetricGroup === groupName 
                    ? 'bg-primary-600 text-text-inverse shadow-md' 
                    : 'bg-bg-secondary text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
                }`}
                onClick={() => handleMetricGroupChange(groupName)}
              >
                {groupName}
              </button>
            ))}
          </div>
        )}
        
        {/* Metric Category Cards */}
        {!compact && activeMetricGroup === 'همه سوالات' && (
          <div className="mb-6">
            <button
              onClick={() => setIsAllQuestionsCollapsed(!isAllQuestionsCollapsed)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-bg-secondary hover:bg-bg-tertiary transition-colors text-text-primary font-medium mb-4"
            >
              <span>{isAllQuestionsCollapsed ? '▼' : '▲'}</span>
              <span>{isAllQuestionsCollapsed ? 'نمایش جزئیات' : 'بستن جزئیات'}</span>
            </button>
            {!isAllQuestionsCollapsed && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedMetricCategories.map((category, index) => {
                  const averageStr = calculateMetricAverage(category);
                  const average = parseFloat(averageStr.toString());
                  const variantClass = average >= 85 ? 'card-variant-success' : 
                                    average >= 70 ? 'card-variant-info' : 
                                    average >= 60 ? 'card-variant-warning' : 
                                    'card-variant-error';
                  return (
                    <div key={category} className={`card-base card-padding ${variantClass}`}>
                      <div className="text-sm text-text-secondary mb-2">{category}</div>
                      <div className="text-2xl font-bold">{averageStr}%</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        {/* Regular Metric Category Cards for other groups */}
        {!compact && activeMetricGroup !== 'همه سوالات' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {selectedMetricCategories.map((category, index) => {
              const averageStr = calculateMetricAverage(category);
              const average = parseFloat(averageStr.toString());
              const variantClass = average >= 85 ? 'card-variant-success' : 
                                average >= 70 ? 'card-variant-info' : 
                                average >= 60 ? 'card-variant-warning' : 
                                'card-variant-error';
              return (
                <div key={category} className={`card-base card-padding ${variantClass}`}>
                  <div className="text-sm text-text-secondary mb-2">{category}</div>
                  <div className="text-2xl font-bold">{averageStr}%</div>
                </div>
              );
            })}
          </div>
        )}
        
        <div className={compact ? "h-60" : "h-96"}>
          <Line 
            data={detailedChartData} 
            options={chartOptions} 
            height={compact ? 240 : 400}
          />
        </div>
      </div>
    </>
  );
};

export default DetailedSatisfaction;